import { useEffect, useState, type ReactNode } from 'react';
import type { BoardroomCoverage, BoardroomRoomBrief } from '../../../types';
import { cutoff, heardLabel, isOlder } from './painTimeline';
import { trendWords } from './StoryReport';
import { isStoryRoom } from '../deliverables/views';

const HIDDEN_KEY = 'jcseo.boardroom.brief.hidden';

function storedHidden(): boolean {
  try {
    return window.localStorage.getItem(HIDDEN_KEY) === '1';
  } catch {
    return false;
  }
}

/** "12,120 / mo". */
export function perMonth(volume: number): string {
  return `${volume.toLocaleString('en-US')} / mo`;
}

/** "free – $249/month", or one price when the ends are the same. */
export function priceRange(low: string | null, high: string | null): string | null {
  if (!low) return null;
  return !high || high === low ? low : `${low} – ${high}`;
}

/** "How sure: early (prices found for 4 of 77)", or null with nothing to go
 *  on. Under a third is early, as the tables' "What this means" counts it. Pure. */
export function sureLine(c: BoardroomCoverage | null | undefined): string | null {
  if (!c || !c.of) return null;
  const share = c.have / c.of;
  const word = share < 1 / 3 ? 'early' : share < 2 / 3 ? 'fair' : 'good';
  return `How sure: ${word} (${c.label} for ${c.have.toLocaleString('en-US')} of ${c.of.toLocaleString('en-US')})`;
}

function Section({
  title,
  link,
  onLink,
  children,
  testId,
  sure,
}: {
  title: string;
  link: string;
  onLink: () => void;
  children: ReactNode;
  testId: string;
  /** What the section's numbers rest on (`sureLine`). */
  sure?: BoardroomCoverage | null;
}) {
  const sureText = sureLine(sure);
  return (
    <section
      className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800"
      data-testid={testId}
    >
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</h3>
      <div className="flex-1 text-sm text-gray-800 dark:text-gray-100">{children}</div>
      {sureText && <p className="text-[11px] text-gray-500 dark:text-gray-400">{sureText}</p>}
      <button
        type="button"
        onClick={onLink}
        className="self-start text-xs text-indigo-600 hover:underline dark:text-indigo-300"
      >
        {link} →
      </button>
    </section>
  );
}

const Empty = ({ children }: { children: ReactNode }) => (
  <p className="text-sm text-gray-400 dark:text-gray-500">{children}</p>
);

/**
 * A STORY ROOM'S BRIEF: why it is news now, who is in it, the newest thing said
 * on the record, what people say, and the best angle to write - the top lines
 * of the story tables (`boardroom/summary.py` `_story`).
 */
export function StoryGrid({
  data,
  onOpenTable,
  onOpenItem,
}: {
  data: BoardroomRoomBrief;
  onOpenTable: (kind: string) => void;
  onOpenItem: (id: number) => void;
}) {
  const s = data.story;
  const why = s && 'trend' in s.why_now ? s.why_now : null;
  const words = trendWords(why?.trend?.change_pct);
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(16rem,1fr))] gap-3">
      <Section title="Why now" link="Story so far" onLink={() => onOpenTable('story')} testId="brief-why-now">
        {why?.trend || why?.latest ? (
          <>
            {why?.trend && (
              <p>
                “{why.trend.keyword}” {words ? `is ${words}` : 'is being tracked'}
                {why.trend.rising.length > 0 && (
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    rising: {why.trend.rising.slice(0, 3).join(' · ')}
                  </span>
                )}
              </p>
            )}
            {why?.latest && (
              <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                Latest:{' '}
                <button type="button" onClick={() => onOpenItem(why.latest!.id)} className="hover:underline">
                  {why.latest.title}
                </button>{' '}
                ({heardLabel(why.latest.happened_at)})
              </p>
            )}
          </>
        ) : (
          <Empty>Nothing on the timeline or trend yet.</Empty>
        )}
      </Section>
      <Section title="Who" link="Who" onLink={() => onOpenTable('people')} testId="brief-who">
        {s && s.people.length ? (
          <ul className="flex flex-col gap-1">
            {s.people.map(p => (
              <li key={p.id}>
                <button type="button" onClick={() => onOpenItem(p.id)} className="hover:underline">
                  {p.title}
                </button>
                {p.role && <span className="text-xs text-gray-500 dark:text-gray-400"> · {p.role}</span>}
              </li>
            ))}
          </ul>
        ) : (
          <Empty>Nobody in the story yet.</Empty>
        )}
      </Section>
      <Section title="On the record" link="Who" onLink={() => onOpenTable('people')} testId="brief-on-record">
        {s?.quote ? (
          <p className="text-sm">
            “{s.quote.quote}”
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              {s.quote.by}, {s.quote.said_at ? heardLabel(s.quote.said_at) : 'date unknown'}
            </span>
          </p>
        ) : (
          <Empty>Nothing said on the record yet.</Empty>
        )}
      </Section>
      <Section title="What people say" link="What people say" onLink={() => onOpenTable('pain_gap')} testId="brief-says">
        {data.complaints.top.length ? (
          <ul className="flex flex-col gap-1">
            {data.complaints.top.map(p => (
              <li key={p.id}>
                <button type="button" onClick={() => onOpenItem(p.id)} className="text-left hover:underline">
                  {p.title}
                </button>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {' '}
                  · {p.voices} {p.voices === 1 ? 'voice' : 'voices'}
                  {p.last_heard && ` · ${heardLabel(p.last_heard)}`}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <Empty>Nothing said about it yet.</Empty>
        )}
      </Section>
      <Section title="Angle to write" link="Angles to write" onLink={() => onOpenTable('angles')} testId="brief-angle">
        {s?.angle ? (
          <p>
            <button type="button" onClick={() => onOpenItem(s.angle!.id)} className="text-left hover:underline">
              {s.angle.title}
            </button>
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              {s.angle.volume ? `${s.angle.volume.toLocaleString('en-US')} searches/mo` : 'no volume yet'}
              {s.angles > 1 && ` · ${s.angles - 1} more`}
            </span>
          </p>
        ) : (
          <Empty>No angles yet: the coverage watcher reads the searches on the next dig.</Empty>
        )}
      </Section>
    </div>
  );
}

/**
 * THE BRIEF: what the room found, on one screen, above the cards.
 *
 * Five tables each answered one question, and learning who leads, what it
 * costs, what customers complain about and how the field sells meant opening
 * all of them. These are their top lines, worked out on the server from the
 * same reports (`jcseo/boardroom/summary.py`), each with a link to the table.
 */
export function BriefBody({
  data,
  onOpenTable,
  onOpenItem,
  onFill,
  noticed,
  roomKind,
}: {
  data: BoardroomRoomBrief;
  onOpenTable: (kind: string) => void;
  onOpenItem: (id: number) => void;
  onFill?: () => void;
  /** What the room noticed (`BriefNoticed`), between the sections and the gaps. */
  noticed?: ReactNode;
  /** A story room's Brief leads with the story, not prices and pitches. */
  roomKind?: string | null;
}) {
  const { prices, complaints, selling, unknown } = data;
  const story = isStoryRoom(roomKind);
  const range = priceRange(prices.low, prices.high);
  return (
    <div className="flex flex-col gap-3">
      {story ? (
        <StoryGrid data={data} onOpenTable={onOpenTable} onOpenItem={onOpenItem} />
      ) : (
      <div className="grid grid-cols-[repeat(auto-fit,minmax(16rem,1fr))] gap-3">
        <Section
          title="Most searched competitors"
          link={`All ${data.companies} ${data.companies === 1 ? 'company' : 'companies'}`}
          onLink={() => onOpenTable('competitors')}
          testId="brief-searched"
          sure={data.coverage?.searched}
        >
          {data.most_searched.length ? (
            <ul className="flex flex-col gap-1">
              {data.most_searched.map(c => (
                <li key={c.id} className="flex items-baseline gap-2">
                  <button
                    type="button"
                    onClick={() => onOpenItem(c.id)}
                    className="min-w-0 flex-1 truncate text-left hover:underline"
                  >
                    {c.title}
                  </button>
                  <span className="shrink-0 tabular-nums text-xs text-gray-500 dark:text-gray-400">
                    {perMonth(c.volume)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <Empty>No search numbers for any company yet.</Empty>
          )}
        </Section>

        <Section
          title="What it costs"
          link="Pricing"
          onLink={() => onOpenTable('price_map')}
          testId="brief-prices"
          sure={data.coverage?.prices}
        >
          {range ? (
            <>
              <div className="text-base font-medium">{range}</div>
              <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                {prices.priced} of {prices.products} products publish a price
                {prices.examples.length > 0 && ': '}
                {prices.examples.map((p, i) => (
                  <span key={p.id}>
                    {i > 0 && ', '}
                    {p.title} {p.label}
                  </span>
                ))}
                .
              </p>
            </>
          ) : (
            <Empty>No prices found yet{prices.products ? ` for any of ${prices.products} products` : ''}.</Empty>
          )}
        </Section>

        <Section
          title="What customers complain about"
          link="Complaints"
          onLink={() => onOpenTable('pain_gap')}
          testId="brief-complaints"
          sure={data.coverage?.complaints}
        >
          {complaints.top.length ? (
            <ul className="flex flex-col gap-1.5">
              {complaints.top.map(p => (
                <li key={p.id}>
                  <button type="button" onClick={() => onOpenItem(p.id)} className="text-left hover:underline">
                    {p.title}
                  </button>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {' '}
                    · {p.voices ? `${p.voices} ${p.voices === 1 ? 'voice' : 'voices'}` : 'no customer voices yet'}
                    {p.unfixed && <span className="text-amber-700 dark:text-amber-300"> · nobody fixes it yet</span>}
                    {p.last_heard && isOlder(p.last_heard, cutoff('2y')) && (
                      <span className="text-gray-400"> · last heard {heardLabel(p.last_heard)}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <Empty>No complaints found yet.</Empty>
          )}
        </Section>

        <Section
          title="How they sell"
          link="How they sell"
          onLink={() => onOpenTable('appeal')}
          testId="brief-selling"
          sure={data.coverage?.selling}
        >
          {selling.top.length ? (
            <>
              <p>
                Most lead with{' '}
                {selling.top.map((a, i) => (
                  <span key={a.angle}>
                    {i > 0 && (i === selling.top.length - 1 ? ' and ' : ', ')}
                    <strong className="font-medium">{a.label}</strong>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {' '}
                      ({a.companies}
                      {i === 0 ? ` of ${selling.read}` : ''})
                    </span>
                  </span>
                ))}
                .
              </p>
              {selling.unused.length > 0 && (
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                  Nobody leads with: {selling.unused.join(', ')}.
                </p>
              )}
            </>
          ) : (
            <Empty>No company's pitch has been read yet.</Empty>
          )}
        </Section>
      </div>
      )}

      {noticed}

      {unknown.total > 0 && (
        <div
          className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs text-gray-600 dark:border-gray-600 dark:text-gray-300"
          data-testid="brief-unknown"
        >
          <span className="font-medium text-gray-700 dark:text-gray-200">Still unknown:</span>
          <span className="min-w-0 flex-1">
            {unknown.top.map(g => `${g.count} missing ${g.words}`).join(' · ')}
            {unknown.total > unknown.top.reduce((n, g) => n + g.count, 0) && ' · and more'}
          </span>
          {onFill && (
            <button
              type="button"
              onClick={onFill}
              className="shrink-0 text-indigo-600 hover:underline dark:text-indigo-300"
            >
              Fill these on the next dig →
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** The Brief, fetched for itself, with a Hide that is remembered per viewer. */
export default function RoomBrief({
  load,
  refreshKey,
  onOpenTable,
  onOpenItem,
  onFill,
  noticed,
  roomKind,
}: {
  noticed?: ReactNode;
  /** A story room's Brief leads with the story. */
  roomKind?: string | null;
  load: () => Promise<BoardroomRoomBrief>;
  /** Changes when the room's findings may have: the Brief loads again. */
  refreshKey: string;
  onOpenTable: (kind: string) => void;
  onOpenItem: (id: number) => void;
  onFill?: () => void;
}) {
  const [data, setData] = useState<BoardroomRoomBrief | null>(null);
  const [hidden, setHidden] = useState(storedHidden);
  useEffect(() => {
    if (hidden) return;
    let alive = true;
    load()
      .then(d => alive && setData(d))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
    // `load` is a fresh closure each render; the key says when to reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, hidden]);
  const toggle = () => {
    setHidden(h => {
      try {
        window.localStorage.setItem(HIDDEN_KEY, h ? '0' : '1');
      } catch {
        /* private mode: per visit */
      }
      return !h;
    });
  };
  return (
    <div className="mb-4" data-testid="room-brief">
      <div className="mb-2 flex items-baseline gap-2">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Brief</h2>
        {!hidden && (
          <span className="text-xs text-gray-500 dark:text-gray-400">What the room found, on one screen</span>
        )}
        <button
          type="button"
          onClick={toggle}
          className="ml-auto text-xs text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100"
          data-testid="brief-toggle"
        >
          {hidden ? 'Show' : 'Hide'}
        </button>
      </div>
      {!hidden &&
        (data ? (
          <BriefBody
            data={data}
            onOpenTable={onOpenTable}
            onOpenItem={onOpenItem}
            onFill={onFill}
            noticed={noticed}
            roomKind={roomKind}
          />
        ) : (
          <p className="text-xs text-gray-400">Reading the room…</p>
        ))}
    </div>
  );
}
